/**
 * CoordinateManager — 1:1 port of ui/src/viewer/coordinates.js from the
 * desktop viewer.  Web-only differences:
 *   - loadOffset() takes a URL and uses fetch (no window.electronAPI.readFile)
 *   - Geoid grid file loading is dropped (relies on a bundled .gtx in
 *     Electron); manual geoid undulation value still works.
 */

import { Vector3 } from 'three'
import proj4 from 'proj4'
import {
  CRS_DEFS,
  HeightSystem,
  HeightSystemValue,
  detectCRS,
  ecefToGeodeticDeg,
  geodeticDegToEcef,
  getAxisLabels,
  transformCoords,
} from './crs'

export interface EcefTransformParams {
  center: [number, number, number]
  rot: number[]            // 9 numbers, column-major (east, north, up)
  gammaRad: number
}

export interface DisplayCoord {
  easting: number
  northing: number
  altitude: number
}

export class CoordinateManager {
  eastingOffset = 0
  northingOffset = 0
  altitudeOffset = 0
  loaded = false

  sourceCRS = 'EPSG:25832'
  displayCRS = 'EPSG:25832'
  heightSystem: HeightSystemValue = HeightSystem.ELLIPSOIDAL
  geoidUndulation = 0

  // ECEF exact-mode state — float64 throughout
  private _ecefMode = false
  private _ecefCenter: [number, number, number] | null = null
  private _rotEnuToEcef: number[] | null = null
  private _cosGamma = 1
  private _sinGamma = 0

  setEcefTransform({ center, rot, gammaRad }: EcefTransformParams): void {
    this._ecefCenter = [center[0], center[1], center[2]]
    this._rotEnuToEcef = rot.slice(0, 9)
    this._cosGamma = Math.cos(gammaRad)
    this._sinGamma = Math.sin(gammaRad)
    this._ecefMode = true
  }

  /** Web: fetch a remote offset.json or .utm_offset.txt and parse it. */
  async loadOffset(url: string): Promise<string | null> {
    try {
      const resp = await fetch(url)
      if (!resp.ok) return null
      const text = await resp.text()

      // JSON format (offset.json from DJI Terra converter)
      if (text.trim().startsWith('{')) {
        try {
          const json = JSON.parse(text)
          if (json.easting_offset != null) this.eastingOffset = json.easting_offset
          if (json.northing_offset != null) this.northingOffset = json.northing_offset
          if (json.altitude_offset != null) this.altitudeOffset = json.altitude_offset
          this.loaded = true
          if (json.utm_epsg) {
            const wgs84Map: Record<string, string> = {
              '32632': 'EPSG:25832',
              '32633': 'EPSG:25833',
            }
            const epsgStr = String(json.utm_epsg)
            const crs =
              wgs84Map[epsgStr] ??
              (CRS_DEFS[`EPSG:${epsgStr}`] ? `EPSG:${epsgStr}` : null)
            if (crs) {
              this.sourceCRS = crs
              this.displayCRS = crs
            }
          }
          return text
        } catch {
          /* fall through to text parser */
        }
      }

      // Text format
      for (const line of text.split('\n')) {
        const trimmed = line.trim()
        if (!trimmed || trimmed.startsWith('#')) continue
        const parts = trimmed.split(/\s+/)
        if (parts.length < 2) continue
        const key = parts[0].toLowerCase()
        const val = parseFloat(parts[1])
        if (isNaN(val)) continue
        if (key.includes('easting')) this.eastingOffset = val
        else if (key.includes('northing')) this.northingOffset = val
        else if (key.includes('altitude')) this.altitudeOffset = val
      }
      this.loaded = true

      const filename = url.split('/').pop() ?? ''
      this.sourceCRS = detectCRS(filename, text)
      this.displayCRS = this.sourceCRS
      return text
    } catch (err) {
      console.warn('Failed to load offset:', err)
      return null
    }
  }

  // ── CRS setters ──────────────────────────────────

  setSourceCRS(code: string): void {
    if (CRS_DEFS[code]) this.sourceCRS = code
  }
  setDisplayCRS(code: string): void {
    if (CRS_DEFS[code]) this.displayCRS = code
  }
  setHeightSystem(system: HeightSystemValue, geoidN: number = 0): void {
    this.heightSystem = system
    this.geoidUndulation = geoidN
  }

  // ── Coordinate conversions ───────────────────────

  private _localToEcef(point: Vector3): [number, number, number] {
    const c = this._cosGamma
    const s = this._sinGamma
    const ex = point.x * c - point.y * s
    const ey = point.x * s + point.y * c
    const ez = point.z
    const r = this._rotEnuToEcef!
    const dx = ex * r[0] + ey * r[3] + ez * r[6]
    const dy = ex * r[1] + ey * r[4] + ez * r[7]
    const dz = ex * r[2] + ey * r[5] + ez * r[8]
    return [
      this._ecefCenter![0] + dx,
      this._ecefCenter![1] + dy,
      this._ecefCenter![2] + dz,
    ]
  }

  private _ecefToLocal(ecefX: number, ecefY: number, ecefZ: number): Vector3 {
    const dx = ecefX - this._ecefCenter![0]
    const dy = ecefY - this._ecefCenter![1]
    const dz = ecefZ - this._ecefCenter![2]
    const r = this._rotEnuToEcef!
    const ex = dx * r[0] + dy * r[1] + dz * r[2]
    const ey = dx * r[3] + dy * r[4] + dz * r[5]
    const ez = dx * r[6] + dy * r[7] + dz * r[8]
    const c = this._cosGamma
    const s = this._sinGamma
    return new Vector3(ex * c + ey * s, -ex * s + ey * c, ez)
  }

  centeredToSource(point: Vector3): DisplayCoord {
    if (this._ecefMode) return this._exactConvert(point, this.sourceCRS)
    return {
      easting: point.x + this.eastingOffset,
      northing: point.y + this.northingOffset,
      altitude: point.z + this.altitudeOffset,
    }
  }

  centeredToDisplay(point: Vector3): DisplayCoord {
    if (this._ecefMode) return this._exactConvert(point, this.displayCRS)
    const source = this.centeredToSource(point)
    const { easting, northing } = transformCoords(
      source.easting,
      source.northing,
      this.sourceCRS,
      this.displayCRS,
    )
    const N = this._getGeoidN()
    return { easting, northing, altitude: source.altitude - N }
  }

  private _exactConvert(point: Vector3, targetCRS: string): DisplayCoord {
    const [ex, ey, ez] = this._localToEcef(point)
    const { lat, lon, alt } = ecefToGeodeticDeg(ex, ey, ez)
    const N = this._getGeoidNLatLon(lat, lon)
    const altitude = alt - N
    if (targetCRS === 'LOCAL') {
      return {
        easting: point.x + this.eastingOffset,
        northing: point.y + this.northingOffset,
        altitude,
      }
    }
    const [easting, northing] = proj4('EPSG:4326', targetCRS, [lon, lat])
    return { easting, northing, altitude }
  }

  displayToCentered(easting: number, northing: number, altitude: number): Vector3 {
    if (this._ecefMode) return this._exactReverse(easting, northing, altitude)

    let srcE: number, srcN: number
    if (
      this.sourceCRS === this.displayCRS ||
      this.sourceCRS === 'LOCAL' ||
      this.displayCRS === 'LOCAL'
    ) {
      srcE = easting
      srcN = northing
    } else {
      const [x, y] = proj4(this.displayCRS, this.sourceCRS, [easting, northing])
      srcE = x
      srcN = y
    }
    const N = this._getGeoidN()
    const srcAlt = altitude + N
    return new Vector3(
      srcE - this.eastingOffset,
      srcN - this.northingOffset,
      srcAlt - this.altitudeOffset,
    )
  }

  private _exactReverse(easting: number, northing: number, altitude: number): Vector3 {
    if (this.displayCRS === 'LOCAL') {
      return new Vector3(
        easting - this.eastingOffset,
        northing - this.northingOffset,
        altitude - this.altitudeOffset,
      )
    }
    const [lonDeg, latDeg] = proj4(this.displayCRS, 'EPSG:4326', [easting, northing])
    const N = this._getGeoidNLatLon(latDeg, lonDeg)
    const ellipsoidalAlt = altitude + N
    const { x, y, z } = geodeticDegToEcef(latDeg, lonDeg, ellipsoidalAlt)
    return this._ecefToLocal(x, y, z)
  }

  private _getGeoidN(): number {
    if (this.heightSystem !== HeightSystem.DHHN2016) return 0
    return this.geoidUndulation
  }

  private _getGeoidNLatLon(_latDeg: number, _lonDeg: number): number {
    if (this.heightSystem !== HeightSystem.DHHN2016) return 0
    return this.geoidUndulation
  }

  getCenterGeoidN(): number {
    return this.geoidUndulation
  }

  getDisplayOffset(): DisplayCoord {
    if (this._ecefMode) {
      const { lat, lon, alt } = ecefToGeodeticDeg(
        this._ecefCenter![0],
        this._ecefCenter![1],
        this._ecefCenter![2],
      )
      if (this.displayCRS === 'LOCAL') {
        return {
          easting: this.eastingOffset,
          northing: this.northingOffset,
          altitude: alt,
        }
      }
      const [easting, northing] = proj4('EPSG:4326', this.displayCRS, [lon, lat])
      return { easting, northing, altitude: alt }
    }
    const { easting, northing } = transformCoords(
      this.eastingOffset,
      this.northingOffset,
      this.sourceCRS,
      this.displayCRS,
    )
    return { easting, northing, altitude: this.altitudeOffset }
  }

  getDisplayLabels(): { easting: string; northing: string } {
    return getAxisLabels(this.displayCRS)
  }

  getUtmZoneNumber(): string | null {
    const def = CRS_DEFS[this.displayCRS]
    if (!def || !def.proj4) return null
    if (!def.proj4.includes('+proj=utm')) return null
    const m = def.proj4.match(/\+zone=(\d+)/)
    return m ? m[1] : null
  }

  distance3D(a: Vector3, b: Vector3): number {
    const dx = a.x - b.x
    const dy = a.y - b.y
    const dz = a.z - b.z
    return Math.sqrt(dx * dx + dy * dy + dz * dz)
  }
}

// Backwards-compat alias — some Lite files imported `Coordinates`
export { CoordinateManager as Coordinates }
